import React, { createContext, useState, useContext } from 'react';

const TestContext = createContext();

export const useTest = () => useContext(TestContext);

export const TestProvider = ({ children }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userName, setUserName] = useState("");
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState('start'); // start, questions, name, loading, result

  // 題目列表 (12題測驗問題)
  const questions = [
    {
      id: 1,
      text: "你和戀人一起看電影時，哪種互動讓你更喜歡？",
      options: [
        { id: 'A', text: "戀人摟著你、牽著你的手。", type: "身體" },
        { id: 'B', text: "戀人全程不看手機，專心陪你看電影。", type: "時間" }
      ]
    },
    {
      id: 2,
      text: "你更希望戀人如何向你表達愛？",
      options: [
        { id: 'A', text: "幫你完成一些瑣事，讓你生活更輕鬆。", type: "服務" },
        { id: 'B', text: "說很多好聽的話，讓你覺得自己被珍視。", type: "讚美" }
      ]
    },
    {
      id: 3,
      text: "當你生日時，哪種方式更能讓你感受到戀人的心意？",
      options: [
        { id: 'A', text: "一整天專屬於你們兩個，一起度過特別的時光。", type: "時間" },
        { id: 'B', text: "戀人精心準備了一份特別的禮物給你。", type: "禮物" }
      ]
    },
    {
      id: 4,
      text: "你希望戀人更常對你做哪件事？",
      options: [
        { id: 'A', text: "時不時擁抱你，給你身體上的安全感。", type: "身體" },
        { id: 'B', text: "替你分擔家務或生活瑣事，讓你更輕鬆。", type: "服務" }
      ]
    },
    {
      id: 5,
      text: "戀人對你的愛，應該更像哪種表現？",
      options: [
        { id: 'A', text: "願意主動幫你解決問題，讓你覺得他/她很可靠。", type: "服務" },
        { id: 'B', text: "會經常誇獎你，讓你覺得自己很有魅力。", type: "讚美" }
      ]
    },
    {
      id: 6,
      text: "如果戀人要為你準備一份驚喜，你更喜歡哪種方式？",
      options: [
        { id: 'A', text: "送你一份精心準備的禮物，代表你們的感情。", type: "禮物" },
        { id: 'B', text: "在你沒注意時，偷偷做一件貼心的事幫助你。", type: "服務" }
      ]
    },
    {
      id: 7,
      text: "你和戀人久未見面，見面時你會更期待？",
      options: [
        { id: 'A', text: "一個大大的擁抱或親吻。", type: "身體" },
        { id: 'B', text: "戀人看著你說：「我真的很想你。」", type: "讚美" }
      ]
    },
    {
      id: 8,
      text: "當你和戀人發生小爭執，你更希望他/她怎麼做？",
      options: [
        { id: 'A', text: "緊緊抱住你，不讓你生氣。", type: "身體" },
        { id: 'B', text: "耐心溝通，確保問題真正解決。", type: "時間" }
      ]
    },
    {
      id: 9,
      text: "你更希望戀人在見到你時的第一反應是？",
      options: [
        { id: 'A', text: "給你一個擁抱或親吻。", type: "身體" },
        { id: 'B', text: "熱情地稱讚你今天的打扮。", type: "讚美" }
      ]
    },
    {
      id: 10,
      text: "戀人對你的體貼，哪種更能讓你感到被愛？",
      options: [
        { id: 'A', text: "幫你做你懶得做的事，比如打掃房間。", type: "服務" },
        { id: 'B', text: "總是不吝嗇對你的稱讚。", type: "讚美" }
      ]
    },
    {
      id: 11,
      text: "如果戀人想讓你開心，你更希望他/她怎麼做？",
      options: [
        { id: 'A', text: "計畫一次浪漫的約會，讓你們有美好的回憶。", type: "時間" },
        { id: 'B', text: "買你最喜歡的東西送你，讓你知道他/她很在乎你。", type: "禮物" }
      ]
    },
    {
      id: 12,
      text: "當你和戀人分開一段時間，你最希望對方怎麼表達愛？",
      options: [
        { id: 'A', text: "買個小禮物送給你，讓你感覺他/她一直記得你。", type: "禮物" },
        { id: 'B', text: "每天至少找一點時間跟你聊天，保持聯繫。", type: "時間" }
      ]
    }
  ];

  // 前進到下一題
  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setStage('name');
    }
  };

  // 返回上一題
  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // 設置答案
  const setAnswer = (questionId, optionType) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionType
    }));
  };

  // 計算測驗結果
  const calculateResult = () => {
    // 統計每種類型的選擇次數
    const counts = {
      "身體": 0,
      "時間": 0,
      "禮物": 0,
      "服務": 0,
      "讚美": 0
    };

    Object.values(answers).forEach(type => {
      counts[type] = (counts[type] || 0) + 1;
    });

    // 找出最多的類型
    let maxType = "身體";
    let maxCount = 0;

    for (const [type, count] of Object.entries(counts)) {
      if (count > maxCount) {
        maxType = type;
        maxCount = count;
      }
    }

    // 返回結果對應資訊
    const resultTypes = {
      "身體": "擁抱系戀人",
      "時間": "時光系戀人",
      "禮物": "驚喜系戀人",
      "服務": "行動系戀人",
      "讚美": "彩虹系戀人"
    };

    const resultAnimals = {
      "身體": "無尾熊",
      "時間": "狗",
      "禮物": "貓",
      "服務": "公牛",
      "讚美": "狐狸"
    };

    const resultColors = {
      "身體": "粉紅色",
      "時間": "暖橘色",
      "禮物": "紫色",
      "服務": "綠色",
      "讚美": "金色"
    };

    const resultSentences = {
      "身體": "愛，就是時時刻刻黏著你！",
      "時間": "有你在，做什麼都幸福。",
      "禮物": "愛，就是用心準備的每個驚喜！",
      "服務": "不說愛，但用一切行動證明！",
      "讚美": "甜言蜜語，不只是說說而已！"
    };

    const resultKeywords = {
      "身體": "擁抱、牽手、肢體接觸",
      "時間": "陪伴、聊天、穩定的相處",
      "禮物": "禮物、儀式感、特別的用心",
      "服務": "幫助、行動力、默默付出",
      "讚美": "讚美、鼓勵、言語上的肯定"
    };

    const resultPoems = {
      "身體": "雙手緊握心不移，深情相依最可期。",
      "時間": "長相廝守喜相依，細水長流最相宜。",
      "禮物": "花開一瞬勝千秋，驚喜情深長伴留。",
      "服務": "默默耕耘無所求，細水長流愛長留。",
      "讚美": "良言一句三冬暖，愛語輕聲解心煩。"
    };
    
    const resultExplanations = {
      "身體": "你是一個需要肢體接觸來感受愛的人！擁抱、牽手、靠在對方肩膀上，這些小舉動能讓你瞬間安心。沒有肢體接觸的愛情，對你來說就像沒電的手機，完全無法運作！擁抱、牽手、親親，這些小小的肢體接觸對你來說，比千言萬語更能傳達愛意！",
      "時間": "對你來說，陪伴才是最好的愛情語言。只要戀人願意花時間陪你，就算只是一起發呆，你都覺得超幸福。沒有時間經營的感情，對你來說完全沒安全感！不論是約會、聊天，還是一起待著什麼都不做，重點是對方願意為你花時間！",
      "禮物": "你是個重視「禮物、驚喜、儀式感」的人！一場浪漫的燭光晚餐、一份用心準備的小禮物，都能讓你感受到戀人的用心。沒有儀式感的戀愛，對你來說就像沒開封的紅包，少了那份期待感！它不一定要昂貴，但一定要用心！無論是節日驚喜，還是隨手的小禮物，這些細節都讓你感受到自己被在乎！",
      "服務": "你相信「愛不是說說而已」，而是要用行動來證明！你最喜歡戀人幫你做貼心的小事，像是準備早餐、幫你修東西、接送你上下班。沒有行動的愛情，對你來說就像空氣炸鍋——看起來厲害但實際上沒什麼溫度！從幫你跑腿買東西，到幫你解決煩心事，這些貼心舉動，才是你感受到愛的最佳方式！",
      "讚美": "你最需要的就是「戀人的言語肯定」！一句「你今天好美」、「你是世界上最棒的」，比任何禮物都更能讓你心花怒放。沒有甜言蜜語的愛情，對你來說就像無字的情書——少了靈魂！無論是「你好棒！」還是「我愛你」，這些話語就像陽光一樣，讓你的世界變得更美好！"
    };

    const resultZonghanExamples = {
      "身體": "就像宗翰每次做錯事，都會一把抱住慧婷想轉移注意力一樣（但希望你不要像他這麼愛耍小聰明啦）！",
      "時間": "就像宗翰每次惹慧婷生氣，為了哄她，總會裝可憐說：「我願意用一輩子的時間來補償妳！」（結果下一秒手機直接爆炸！）",
      "禮物": "就像宗翰每次出事後，都會立刻買東西想賄賂慧婷一樣（但希望你別像他這麼蠢啦）！",
      "服務": "就像宗翰每次惹慧婷生氣，都會幫她跑腿買東西、倒垃圾、洗碗，試圖用「勞動換和解」（但其實問題根本沒解決）。",
      "讚美": "就像宗翰每次想哄慧婷，都會狂誇她 100 遍：「妳今天真的超級無敵漂亮！」，結果誇到第 101 句時……桌子突然爆炸！"
    };

    const resultYuelaoReminders = {
      "身體": "身體接觸很重要，但愛需要適當的空間。學會讀懂對方的情緒，別讓擁抱變成壓力！",
      "時間": "愛情不只是形影不離，而是彼此願意花時間經營關係。學會尊重彼此的節奏！",
      "禮物": "儀式感很重要，但愛情的價值不該被物質綁架！試著感受戀人在其他方面的用心。",
      "服務": "愛是雙向的，不是單方面付出才叫愛。也要記得適時地為對方付出喔!",
      "讚美": "甜言蜜語讓人開心，但愛不只有語言。學會從行動、眼神、態度中感受對方的真心！"
    };

    return {
      type: resultTypes[maxType],
      animal: resultAnimals[maxType],
      color: resultColors[maxType],
      sentence: resultSentences[maxType],
      keywords: resultKeywords[maxType],
      poem: resultPoems[maxType],
      explanation: resultExplanations[maxType],
      zonghanExample: resultZonghanExamples[maxType],
      yuelaoReminder: resultYuelaoReminders[maxType],
      originalType: maxType
    };
  };

  // 新增函數：計算並設置結果，但不改變階段
  const calculateAndSetResult = () => {
    const calculatedResult = calculateResult();
    setResult(calculatedResult);
  };

  // 提交測驗
  const submitTest = () => {
    setStage('loading');
    
    // 模擬載入時間
    setTimeout(() => {
      const calculatedResult = calculateResult();
      setResult(calculatedResult);
      setStage('result');
    }, 2000);
  };

  return (
    <TestContext.Provider value={{
      stage,
      setStage,
      currentQuestion,
      questions,
      nextQuestion,
      prevQuestion,
      setAnswer,
      answers,
      userName,
      setUserName,
      submitTest,
      result,
      calculateAndSetResult
    }}>
      {children}
    </TestContext.Provider>
  );
};